import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { MemoryHistory } from 'history';

import { parseDate } from 'common/support/dateHelper';
import { getUpcomingDate } from 'support/eventHelper';
import BrandLogo from 'components/common/tile/BrandLogo';
import Focusable from 'components/focus/Focusable';
import Image from 'components/common/Image';
import { getImageUrl } from 'support/imageHelper';

import { useFocusManager } from 'hooks/focus';
import { useVideoById } from 'hooks/video';

import { useNetworkName } from 'hooks/kitchenSink';
import { KitcenSinkRowItemDataItem, RelatedItem } from 'store-types';
import styles from './UpcomingEvent.module.scss';

interface LocationState {
  redirectTo?: {
    pathname: string;
    search: string;
  };
}

interface ReferringLocation {
  search?: string;
}

const useReferringVideo = (
  referringLocation: ReferringLocation | undefined,
): KitcenSinkRowItemDataItem | RelatedItem | null | undefined => {
  let redirectedVideoId: string | undefined;
  if (referringLocation && referringLocation.search) {
    const params = new URLSearchParams(referringLocation.search);
    redirectedVideoId = params.get('video') ?? undefined;
  }
  const [video] = useVideoById(redirectedVideoId);
  return video;
};

const UpcomingEvent: React.FC = () => {
  const intl = useIntl();
  const history = useHistory() as MemoryHistory;

  const focusManager = useFocusManager();
  useEffect(() => {
    focusManager.setCurrentFocus('upcoming-event-page');
  }, [focusManager]);

  const { contentId } = useParams<{ contentId: string }>();
  const [item] = useVideoById(contentId);

  const { state: routeState } = useLocation<LocationState>();
  const referringVideo = useReferringVideo(routeState?.redirectTo);

  useEffect(() => {
    // transition to event
    if (
      referringVideo?.event?.status === 'pre' ||
      referringVideo?.event?.status === 'active'
    ) {
      history.replace(routeState?.redirectTo || '/', {
        restore: true,
      });
    }
    return () => undefined;
  }, [referringVideo, history, routeState]);

  const {
    about,
    schedule,
    network,
  } = item ?? {};
  const title = about?.title;
  const description = about?.description;
  const videoStartDate = schedule?.videoStartDate;
  const date = getUpcomingDate(intl, parseDate((videoStartDate as number) * 1000));
  const networkTts = useNetworkName(network);

  const ttsText = [date, title, networkTts, description];

  return (
    <Focusable
      focusId="upcoming-event-page"
      className={classNames('w-100', 'h-100')}
      ttsText={ttsText}
      render={() => {
        if (!item) return null;

        const { images } = item;
        const src = images ? getImageUrl(images.baseImage16X9, 1920) : null;

        return (
          <>
            <div
              className={classNames('w-100 h-100 fixed-top', styles.background)}
              data-testid="upcoming-event"
            >
              <Image
                alt="event poster"
                src={src}
                className="w-100 h-100"
                data-testid="event-poster"
              />
            </div>
            <div
              className={classNames(
                'w-100 h-100',
                'd-flex flex-column justify-content-center fixed-top text-center',
                styles.gradient,
              )}
            >
              {date && (
                <div className={styles.startDate} data-testid="upcoming-time">
                  {date}
                </div>
              )}
              <div className={styles.title}>{title}</div>
              <BrandLogo
                className={styles.logo}
                brand={network}
                fixedSize={false}
              />
              <div className={styles.description}>{description}</div>
            </div>
          </>
        );
      }}
    />
  );
};

export default UpcomingEvent;
